
import React, { forwardRef, useCallback } from 'react'
import { WorkspacePanel } from '@genieartinc/studio-ui'
import { Icon } from '@/components/ui/icon'
import { FullSettingsSkeleton } from './components/SettingsSkeleton'
import { CustomizeNodePopover } from './NodeSettingsPanelHeader'
import { NodeSettingsBasicSection } from './NodeSettingsBasicSection'
import { NodeSettingsSystemPrompt, NodeSettingsParameterSection } from './NodeSettingsParameterSection'
import { useNodeSettingsState } from './useNodeSettingsState'
import { PurposeTabs } from './PurposeTabs'
import { TextModeTabs } from './TextModeTabs'
import { useCanvasBridgeStore } from '@/stores/useCanvasBridgeStore'
import { ShaderNodeSettings } from '@/components/canvas/node-settings/shader-node-settings'
import { FrameInputNodeSettings } from '@/components/canvas/node-settings/frame-input-node-settings'
import { FrameOutputNodeSettings } from '@/components/canvas/node-settings/frame-output-node-settings'
import { modelTitleState } from '@/components/canvas/utils/modelTitle'
import { styleTransferPresentationForNode } from '@/lib/canvas/style-transfer-roles'
import { outpaintSourceUrl } from './OutpaintExpansionEditor'
import { StyleTransferInputs } from './StyleTransferInputs'
import { inpaintPresentationForNode } from '@/lib/canvas/inpaint-roles'
import { InpaintInputs } from './InpaintInputs'
import { isInpaintMaskParam, isInpaintSourceParam, ensureInpaintHandles } from '@genieartinc/schemas/pools/ai'
import { isModelFreeNodeControl, nodeControlHelp } from './node-control-help'
import type { NodeSettingsProps, NodeSettingsPanelRef } from './types'

const NodeSettingsPanelComponent = forwardRef<NodeSettingsPanelRef, NodeSettingsProps>(function NodeSettingsPanel(props, ref) {
  const { nodeId, onCollapse, collapsed = false } = props

  const state = useNodeSettingsState(props, ref)

  const handleTextModeChange = useCallback((newMode: string) => {
    if (!nodeId) return
    const swapFn = useCanvasBridgeStore.getState().swapNodeTypeCallback
    if (swapFn) swapFn(nodeId, newMode)
  }, [nodeId])

  const isShader = props.componentType === 'ShaderNode'
  const isFrameInput = props.componentType === 'FrameInput'
  const isFrameOutput = props.componentType === 'FrameOutput'
  const modelFree = isModelFreeNodeControl(props.componentType)
  // Frame and file nodes have nothing to pin. Shader keeps its own settings column.
  const isShortPanel = modelFree && !isShader
  const frameState = props.nodeState ?? {}
  const help = nodeId ? nodeControlHelp({
    componentType: props.componentType,
    nodeType: props.nodeType,
    purpose: props.purpose,
    subPurpose: props.subPurpose,
    model: !modelFree && state.selectedModelId && !state.libraryLoading
      ? { name: state.selectedModelName, description: state.selectedModelDescription }
      : null,
  }) : null
  const identity = state.selectedModelName
    ? modelTitleState({ ...frameState, title: props.nodeTitle }, state.selectedModelName)
    : { title: props.nodeTitle, titleSource: frameState.titleSource, modelName: frameState.modelName }
  const frameStateChange = props.onStateChange ?? (() => {})
  const styleTransfer = styleTransferPresentationForNode({
    subPurpose: props.subPurpose,
    paramNames: state.inputParams?.map((param) => param.name),
    handles: props.nodeHandles,
    nodeId,
    nodes: props.graphNodes,
    edges: props.graphEdges,
  })
  const inpaint = inpaintPresentationForNode({
    subPurpose: props.subPurpose,
    paramNames: state.inputParams?.map((param) => param.name),
    required: state.inputParams?.filter((param) => param.required).map((param) => param.name),
    handles: props.subPurpose === 'inpaint' ? ensureInpaintHandles(props.nodeHandles ?? [], 'inpaint') : props.nodeHandles,
    nodeId,
    nodes: props.graphNodes,
    edges: props.graphEdges,
    savedFields: state.localSettings as Record<string, unknown>,
  })
  const settingsParams = props.subPurpose === 'inpaint'
    ? state.settingsParams.filter((param) => !isInpaintMaskParam(param.name) && !isInpaintSourceParam(param.name))
    : state.settingsParams
  return (
    <WorkspacePanel
      title="Node controls"
      open={!collapsed}
      onOpenChange={onCollapse}
      onClose={props.onClose}
      // With nothing to adjust the panel stays a small hint instead of a full-height empty column.
      className={nodeId && !isShortPanel ? 'min-h-0 flex-1' : 'shrink-0'}
      actions={nodeId && !isShader && !isShortPanel && <CustomizeNodePopover
        settingsParams={settingsParams}
        pinnedParameters={state.pinnedParameters}
        onPinnedParametersBatch={state.setPinnedParametersBatch}
        nodeType={props.nodeType}
      />}
    >
      {nodeId && <div className="border-t border-border px-3 py-2 text-sm text-muted-foreground">
        <p className="truncate" title={identity.title}>{identity.title || 'Selected node'}</p>
        {identity.titleSource !== 'automatic' && typeof identity.modelName === 'string' && <p className="truncate text-xs" title={`Model: ${identity.modelName}`}>Model: {identity.modelName}</p>}
        {help && <p data-testid="node-control-help" className="mt-1 whitespace-normal break-words text-xs leading-snug">{help.operation}</p>}
        {help?.model && <p data-testid="node-control-model-help" className="mt-1 whitespace-normal break-words text-xs leading-snug">{help.model}</p>}
      </div>}
      {nodeId && isShader ? <ShaderNodeSettings
        nodeId={nodeId}
        state={props.nodeState ?? {}}
        onStateChange={props.onStateChange ?? (() => {})}
      /> : nodeId && isFrameInput ? <FrameInputNodeSettings
        nodeId={nodeId}
        state={frameState}
        onStateChange={frameStateChange}
      /> : nodeId && isFrameOutput ? <FrameOutputNodeSettings
        nodeId={nodeId}
        state={frameState}
        onStateChange={frameStateChange}
      /> : <>
            {nodeId && props.nodeType === 'text-llm' && (
              <TextModeTabs activeMode={props.componentType ?? 'TextNode'} onModeChange={handleTextModeChange} />
            )}

            {nodeId && (props.nodeType === 'image' || props.nodeType === 'video' || props.nodeType === '3d' || props.nodeType === 'sound') && (
              <PurposeTabs
                activePurpose={props.purpose ?? 'create'}
                onPurposeChange={state.handlePurposeChange}
              />
            )}

            <div>
              {!nodeId ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground">
                  <Icon name="mouse-pointer-click" className="h-8 w-8 opacity-40" />
                  <span className="text-sm">Select a node to adjust settings</span>
                </div>
              ) : modelFree ? null : state.libraryLoading ? (
                <FullSettingsSkeleton />
              ) : (
                <>
                  <NodeSettingsBasicSection
                    libraryModels={state.libraryModels}
                    libraryLoading={state.libraryLoading}
                    libraryEmpty={state.libraryEmpty}
                    libraryError={state.libraryError}
                    refetchLibrary={state.refetchLibrary}
                    selectedModelId={state.selectedModelId}
                    onModelChange={state.handleModelChange}
                  />

                  {styleTransfer && <StyleTransferInputs view={styleTransfer} />}
                  {inpaint && <InpaintInputs view={inpaint} />}

                  {state.selectedModelId && (
                    <NodeSettingsParameterSection
                      settingsParams={settingsParams}
                      localSettings={state.localSettings}
                      isModelChanging={state.isModelChanging}
                      onSettingsChange={state.handleSettingsChange}
                      pinnedParameters={state.pinnedParameters}
                      onPinToggle={state.handlePinToggle}
                      sourceUrl={outpaintSourceUrl({
                        nodeId,
                        handles: props.nodeHandles,
                        nodes: props.graphNodes,
                        edges: props.graphEdges,
                      })}
                    />
                  )}

                  {state.selectedModelId && state.effectiveNodeType === 'text-llm' && (
                    <NodeSettingsSystemPrompt
                      systemPrompt={state.systemPrompt}
                      onSystemPromptChange={state.handleSystemPromptChange}
                    />
                  )}
                </>
              )}
            </div>
      </>}
    </WorkspacePanel>
  )
})

// A node owns its edit draft and pending save. Switching selection unmounts and
// flushes the previous draft before the next node receives its own settings.
const ScopedNodeSettingsPanel = forwardRef<NodeSettingsPanelRef, NodeSettingsProps>(function ScopedNodeSettingsPanel(props, ref) {
  return <NodeSettingsPanelComponent key={`${props.nodeId ?? 'none'}:${props.componentType ?? 'none'}`} {...props} ref={ref} />
})

/** Shallow comparison for initialParams to avoid expensive JSON.stringify */
function shallowEqualInitialParams(
  a: NodeSettingsProps['initialParams'],
  b: NodeSettingsProps['initialParams'],
): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.modelFamilySlug === b.modelFamilySlug &&
    a.modelId === b.modelId &&
    a.systemPrompt === b.systemPrompt &&
    a.settings === b.settings &&
    a.pinnedParameters === b.pinnedParameters
  )
}

export const NodeSettingsPanel = React.memo(ScopedNodeSettingsPanel, (prev, next) => {
  return (
    prev.nodeId === next.nodeId &&
    prev.nodeType === next.nodeType &&
    prev.componentType === next.componentType &&
    prev.nodeTitle === next.nodeTitle &&
    prev.purpose === next.purpose &&
    prev.subPurpose === next.subPurpose &&
    prev.collapsed === next.collapsed &&
    prev.onCollapse === next.onCollapse &&
    prev.onClose === next.onClose &&
    prev.onStateChange === next.onStateChange &&
    prev.nodeState === next.nodeState &&
    prev.nodeHandles === next.nodeHandles &&
    prev.graphNodes === next.graphNodes &&
    prev.graphEdges === next.graphEdges &&
    shallowEqualInitialParams(prev.initialParams, next.initialParams)
  )
})
